"use client";

import { useEffect, useState } from "react";
import { Copy, ExternalLink, Globe } from "lucide-react";

export default function BrowserBreakout() {
    const [isInAppBrowser, setIsInAppBrowser] = useState(false);
    const [copied, setCopied] = useState(false);
    const [currentUrl, setCurrentUrl] = useState("");
    const [isAndroid, setIsAndroid] = useState(false);

    useEffect(() => {
        // Run only on client
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Common in-app browser identifiers for Instagram, Facebook, Messenger
        const inAppIdentifiers = ["Instagram", "FBAN", "FBAV"];

        // Check if any identifier exists in the user agent string
        const detected = inAppIdentifiers.some((identifier) =>
            userAgent.includes(identifier)
        );

        if (detected) {
            setIsInAppBrowser(true); // eslint-disable-line react-hooks/set-state-in-effect -- runs once on mount
            setCurrentUrl(window.location.href);

            // Check for Android
            const android = /android/i.test(userAgent);
            setIsAndroid(android);

            if (android) {
                // Try to auto-redirect on Android using chrome intent
                const url = window.location.href.replace(/^https?:\/\//, "");
                const intentUrl = `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;
                window.location.href = intentUrl;
            }
        }
    }, []);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(currentUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleAndroidOpen = () => {
        const url = window.location.href.replace(/^https?:\/\//, "");
        const intentUrl = `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;
        window.location.href = intentUrl;
    };

    if (!isInAppBrowser) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-300 font-poppins">
            <div className="max-w-md w-full">
                {/* Icon */}
                <div className="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                    <Globe className="w-10 h-10 text-black" />
                </div>

                {/* Text */}
                <div className="mb-8">
                    <h1 className="font-medium text-[16px] leading-[24px] text-black mb-6">
                        Open in System Browser
                    </h1>
                    <p className="font-normal text-[14px] leading-[22px] text-gray-600">
                        For the best experience (payments, downloads, and login), please open this page in {isAndroid ? "Chrome" : "Safari or Chrome"}.
                    </p>
                </div>

                {/* Android Action */}
                {isAndroid ? (
                    <div className="space-y-4">
                        <button
                            onClick={handleAndroidOpen}
                            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-black text-white rounded-full font-medium text-[14px] hover:bg-gray-900 transition-colors active:scale-95"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Open in Chrome
                        </button>
                        <p className="font-normal text-[14px] leading-[22px] text-gray-500">
                            If the button doesn&apos;t work, tap the menu ••• and select &quot;Open in Chrome&quot;.
                        </p>
                    </div>
                ) : (
                    /* iOS Action */
                    <div className="bg-gray-50 rounded-xl p-6 text-left space-y-4 border border-gray-100 mb-8">
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-4 h-4 bg-black text-white rounded-full flex items-center justify-center font-bold text-[10px] mt-[3px]">
                                1
                            </div>
                            <p className="font-normal text-[14px] leading-[22px] text-gray-700">
                                Tap the <span className="font-semibold">•••</span> or check the menu icon in the corner.
                            </p>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-4 h-4 bg-black text-white rounded-full flex items-center justify-center font-bold text-[10px] mt-[3px]">
                                2
                            </div>
                            <p className="font-normal text-[14px] leading-[22px] text-gray-700">
                                Select <span className="underline decoration-1 underline-offset-2">Open in Browser</span> or <span className="underline decoration-1 underline-offset-2">Open in Safari/Chrome</span>.
                            </p>
                        </div>
                    </div>
                )}

                {/* iOS Fallback Copy Link */}
                {!isAndroid && (
                    <div className="space-y-4">
                        <button
                            onClick={handleCopyLink}
                            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#2B2B2B] text-white rounded-full font-medium text-[14px] hover:opacity-90 transition-opacity active:scale-95"
                        >
                            {copied ? (
                                <>Link Copied!</>
                            ) : (
                                <>
                                    <Copy className="w-4 h-4" />
                                    Copy Link
                                </>
                            )}
                        </button>
                        <p className="font-normal text-[14px] leading-[22px] text-gray-500 text-center">
                            If you can&apos;t find the menu, copy the link and paste it in your browser.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
